import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import APITable from "../../../components/Tables/APITable";
import Pagination from "../../../components/Pagination/Pagination";
import Button from "../../../components/Button/Button";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../../apiService";

/* ===================== STYLES ===================== */
import styles from "./HealthApi.module.scss";

const HealthApi = ({ onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminView = location.state?.isAdminView || false;

  /* ==================================================
     STATE
  ================================================== */
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");

  // Pagination meta
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  /* ==================================================
     TABLE COLUMNS
  ================================================== */
  const columns = [
    { label: "Code", key: "code" },
    { label: "API Name", key: "apiName" },
    ...(isAdminView ? [{ label: "Vendor", key: "vendor" }] : []),
    { label: "Level", key: "level" },
    { label: "UP/Down", key: "api_status" },
    { label: "Up/Down From", key: "downTime" },
    { label: "Last Activity At", key: "downAT" },
  ];

  /* ==================================================
     FETCH API LIST
  ================================================== */
  const fetchApis = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet(
        "catalog",
        `/apidown/list?page=${currentPage}&limit=${rowsPerPage}&search=${searchText}`,
      );

      if (response?.success) {
        const { data, pagination } = response;

        setApis(
          data.map((api) => ({
            id: api.id,
            code: api.code ?? "--",
            level: api.level ?? "--",
            vendor: api.vendor_name ?? "--",
            category_name: api.category_name ?? "--",
            apiName: api.name ?? "--",
            api_status: api.api_status ?? "--",
            active: api.status === "Publish",
            totalCount: api.total_count ?? 0,
            downAT: api.down_at ?? "--",
            downTime: api.down_time ?? "--",
            downactive: api.is_down === true,
          })),
        );

        setPagination(pagination);
      }
    } catch (error) {
      console.error("Failed to fetch API list", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchText]);

  useEffect(() => {
    fetchApis();
  }, [fetchApis]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchText(searchInput);
  };

  /* ==================================================
     TABLE CELL RENDER
  ================================================== */
  const renderCell = (row, key) => {
    if (key === "downAT") {
      const datetime = row.downAT;

      if (!datetime || datetime === "--") return "--";

      const [date, time, meridian] = datetime.split(" ");

      return (
        <span>
          {date}
          <span className={styles["admin-ac-health-api__time"]}>
            {time} {meridian}
          </span>
        </span>
      );
    }

    if (key === "api_status") {
      const value = row.api_status;
      let badgeClass = styles["admin-ac-health-api__badge--default"];

      if (value === "Up") badgeClass = styles["admin-ac-health-api__badge--up"];
      if (value === "Down")
        badgeClass = styles["admin-ac-health-api__badge--down"];

      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            className={`${styles["admin-ac-health-api__badge"]} ${badgeClass}`}
          >
            {value}
          </div>
        </div>
      );
    }

    return row[key];
  };

  /* ==================================================
     JSX
  ================================================== */
  return (
    <div className={styles["admin-ac-health-api__page"]}>
      {/* HEADER */}
      <div className={styles["admin-ac-health-api__header"]}>
        <button
          className={styles["admin-ac-health-api__back-btn"]}
          onClick={() => (onBack ? onBack() : navigate(-1))}
          aria-label="Go Back"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>

        <div className={styles["admin-ac-health-api__title-group"]}>
          <h2 className={styles["admin-ac-health-api__title"]}>API's Health</h2>
          <p className={styles["admin-ac-health-api__subtitle"]}>
            Comprehensive documentation for all Doql APIs
          </p>
        </div>
      </div>

      {/* TABLE CARD */}
      <div className={styles["admin-ac-health-api__card"]}>
        <div className={styles["admin-ac-health-api__controls"]}>
          <div className={styles["admin-ac-health-api__showing-text"]}>
            Showing {apis.length} of {pagination.total} APIs
          </div>

          <div className={styles["admin-ac-health-api__search-group"]}>
            <Input
              type="search"
              placeholder="Search API Name/Code/Vendor Name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Button text="Search" onClick={handleSearch} />
          </div>
        </div>

        {loading ? (
          <TableSkeleton columns={isAdminView ? 7 : 6} rows={6} />
        ) : (
          <APITable
            columns={columns}
            data={apis}
            renderCell={renderCell}
            loading={loading}
          />
        )}

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(limit) => {
            setRowsPerPage(limit);
            setCurrentPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default HealthApi;
